import * as Dialog from "@radix-ui/react-dialog";
import { Repeat } from "lucide-react";
import { useMemo, useState } from "react";
import type { Signature } from "../types/chain";
import { useSignatureSaver } from "../graph/useSignatureSaver";
import { useChainStore } from "../store/chainStore";
import { SignatureGroupIcon } from "./SignatureGroupIcon";

interface SignaturesImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  systemId: string | null;
  systemName: string | null;
  current: Signature[];
  incoming: Signature[];
}

interface Diff {
  adds: Signature[];
  updates: { before: Signature; after: Signature }[];
  removes: Signature[];
}

function mergeSignature(prev: Signature, inc: Signature): Signature {
  const trimmedName = inc.name.trim();
  return {
    scan_id: inc.scan_id,
    name: trimmedName ? trimmedName : prev.name,
    group: inc.group !== "unknown" ? inc.group : prev.group,
  };
}

function diffSignatures(current: Signature[], incoming: Signature[]): Diff {
  const currentById = new Map(current.map((s) => [s.scan_id, s]));
  const incomingById = new Map(incoming.map((s) => [s.scan_id, s]));
  const adds: Signature[] = [];
  const updates: { before: Signature; after: Signature }[] = [];
  const removes: Signature[] = [];
  for (const sig of incoming) {
    const prev = currentById.get(sig.scan_id);
    if (!prev) {
      adds.push(sig);
      continue;
    }
    const merged = mergeSignature(prev, sig);
    if (merged.name !== prev.name || merged.group !== prev.group) {
      updates.push({ before: prev, after: merged });
    }
  }
  for (const sig of current) {
    if (!incomingById.has(sig.scan_id)) removes.push(sig);
  }
  return { adds, updates, removes };
}

export function SignaturesImportDialog({
  open,
  onOpenChange,
  systemId,
  systemName,
  current,
  incoming,
}: SignaturesImportDialogProps) {
  const saver = useSignatureSaver();
  const captureSignaturesUndo = useChainStore((s) => s.captureSignaturesUndo);
  const [confirmRemoves, setConfirmRemoves] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lazyDelete, setLazyDelete] = useState(true);

  const diff = useMemo(
    () => diffSignatures(current, incoming),
    [current, incoming],
  );

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setConfirmRemoves(false);
      setError(null);
      setBusy(false);
      setLazyDelete(true);
    }
    onOpenChange(next);
  };

  const removesGated = lazyDelete && diff.removes.length > 0 && !confirmRemoves;
  const noAddsOrUpdates =
    diff.adds.length === 0 && diff.updates.length === 0;
  const keptByLazyDelete =
    noAddsOrUpdates && !lazyDelete && diff.removes.length > 0;
  const nothingToApply =
    noAddsOrUpdates && (!lazyDelete || diff.removes.length === 0);

  const apply = async () => {
    if (!systemId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const next: Signature[] = [];
      const incomingById = new Map(incoming.map((s) => [s.scan_id, s]));
      for (const sig of current) {
        const inc = incomingById.get(sig.scan_id);
        if (inc) next.push(mergeSignature(sig, inc));
      }
      for (const sig of incoming) {
        if (!current.find((c) => c.scan_id === sig.scan_id)) next.push(sig);
      }
      await saver.save(systemId, next, lazyDelete);
      captureSignaturesUndo(systemId, current);
      handleOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save signatures.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal__overlay" />
        <Dialog.Content
          className="modal__content signatures-import"
          aria-describedby={undefined}
        >
          <div className="signatures-import__title-row">
            <Dialog.Title className="modal__title">
              Import signatures{systemName ? ` — ${systemName}` : ""}
            </Dialog.Title>
            <button
              type="button"
              className={`signatures-import__lazy-toggle${
                lazyDelete ? "" : " signatures-import__lazy-toggle--off"
              }`}
              onClick={() => setLazyDelete((v) => !v)}
              aria-pressed={lazyDelete}
              title={
                lazyDelete
                  ? "Lazy Delete on — missing signatures will be removed"
                  : "Lazy Delete off — missing signatures will be kept"
              }
            >
              <Repeat size={14} aria-hidden />
              <span>{lazyDelete ? "Lazy Delete" : "Lazy Delete OFF"}</span>
            </button>
          </div>

          {nothingToApply && (
            <p className="signatures-import__empty">
              {keptByLazyDelete
                ? `No changes to apply. Lazy Delete is off, so ${diff.removes.length} signature${diff.removes.length === 1 ? "" : "s"} missing from the paste ${diff.removes.length === 1 ? "is" : "are"} kept.`
                : "No changes — pasted set matches the current signatures."}
            </p>
          )}

          {diff.adds.length > 0 && (
            <Section
              title={`Add (${diff.adds.length})`}
              variant="add"
            >
              {diff.adds.map((s) => (
                <Row key={s.scan_id} signature={s} />
              ))}
            </Section>
          )}

          {diff.updates.length > 0 && (
            <Section
              title={`Update (${diff.updates.length})`}
              variant="update"
            >
              {diff.updates.map((u) => (
                <div key={u.after.scan_id} className="signatures-import__update">
                  <Row signature={u.before} muted />
                  <span className="signatures-import__arrow">→</span>
                  <Row signature={u.after} />
                </div>
              ))}
            </Section>
          )}

          {lazyDelete && diff.removes.length > 0 && (
            <Section
              title={`Remove (${diff.removes.length})`}
              variant="remove"
            >
              {diff.removes.map((s) => (
                <Row key={s.scan_id} signature={s} />
              ))}
              <label className="signatures-import__confirm">
                <input
                  type="checkbox"
                  checked={confirmRemoves}
                  onChange={(e) => setConfirmRemoves(e.target.checked)}
                />
                Confirm removal of {diff.removes.length} signature
                {diff.removes.length === 1 ? "" : "s"}
              </label>
            </Section>
          )}

          {error && (
            <p className="form__hint form__hint--error" role="alert">
              {error}
            </p>
          )}

          <div className="modal__actions">
            <button
              type="button"
              className="modal__btn"
              onClick={() => handleOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="modal__btn modal__btn--primary"
              onClick={apply}
              disabled={busy || nothingToApply || removesGated || !systemId}
            >
              {busy ? "Saving…" : "Apply"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Section({
  title,
  variant,
  children,
}: {
  title: string;
  variant: "add" | "update" | "remove";
  children: React.ReactNode;
}) {
  return (
    <section
      className={`signatures-import__section signatures-import__section--${variant}`}
    >
      <h3 className="signatures-import__section-title">{title}</h3>
      {children}
    </section>
  );
}

function Row({
  signature,
  muted,
}: {
  signature: Signature;
  muted?: boolean;
}) {
  return (
    <div
      className={`signatures-import__row${
        muted ? " signatures-import__row--muted" : ""
      }`}
    >
      <SignatureGroupIcon group={signature.group} />
      <code className="signatures-import__scan-id">{signature.scan_id}</code>
      <span className="signatures-import__name">{signature.name || "—"}</span>
      <span className="signatures-import__group">{signature.group}</span>
    </div>
  );
}
